"use client";

import Image from "next/image";
import { useEffect, useState } from "react";

type HeroSlide = { src: string; alt: string; delay: string };

/**
 * pbdSlide fades each photo up from black over the first 3% of its 45s cycle,
 * 1.35s. Starting every slide that far into its cycle means the first photo is
 * fully visible on the very first frame instead of fading in while the visitor
 * waits; the gaps between slides are unchanged.
 */
const FADE_IN_SECONDS = 1.35;

function leadIn(delay: string): string {
  const seconds = Number.parseFloat(delay);
  return Number.isFinite(seconds) ? `${seconds - FADE_IN_SECONDS}s` : delay;
}

/**
 * The homepage hero crossfades through several full-bleed photos, but only
 * the first is ever visible on load — the rest are just stacked at opacity 0
 * waiting for their turn in the CSS animation. Rendering all of them in the
 * initial HTML meant the browser's preloader discovered and started
 * fetching every slide immediately, competing for bandwidth with the one
 * image that actually matters for LCP. Only slide 0 is server-rendered (with
 * `priority`); the rest mount client-side after hydration, so their bytes
 * are never in the initial request wave.
 */
export function HeroSlides({ slides }: { slides: HeroSlide[] }) {
  const [restMounted, setRestMounted] = useState(false);

  useEffect(() => {
    // Next's static prerendering (React 19's prerender/resume APIs) can
    // flush passive effects during the build-time render pass itself, not
    // just in the browser after real hydration — a plain `setRestMounted(true)`
    // here got baked into the static HTML, defeating the deferral entirely.
    // There is never a `window` during that server-side pass, so gating on
    // it keeps the extra slides out of the prerendered output and only
    // mounts them once this genuinely runs in a browser.
    if (typeof window === "undefined") return;
    setRestMounted(true);
  }, []);

  const [first, ...rest] = slides;
  if (!first) return null;

  return (
    <>
      <Image
        key={first.src}
        src={first.src}
        alt={first.alt}
        fill
        priority
        className="pbd-hero-slide pbd-hero-slide--first"
        style={{ animationDelay: leadIn(first.delay) }}
        sizes="100vw"
      />
      {restMounted &&
        rest.map((slide) => (
          <Image
            key={slide.src}
            src={slide.src}
            alt={slide.alt}
            fill
            className="pbd-hero-slide"
            style={{ animationDelay: leadIn(slide.delay) }}
            sizes="100vw"
          />
        ))}
    </>
  );
}
